import { useState, useContext } from "react";
// @ts-ignore - react-router-dom v5 types not installed
import { Redirect } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Mail, Users, Clock } from "lucide-react";

import "./index.css";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { i18n } from "../translate/i18n";
import { Sidebar } from "@/components/Sidebar";
import { Header } from "@/components/Header";
import { WelcomeCard } from "@/components/WelcomeCard";
import { KPICard } from "@/components/KPICard";
import { ConversationsChart, ChannelDonutChart, SLAChart } from "@/components/Charts";
import { RecentAttendance } from "@/components/RecentAttendance";
import { ChannelPerformance } from "@/components/ChannelPerformance";
import { AgentsOnline } from "@/components/AgentsOnline";
import { KanbanBoard } from "@/components/Kanban";
import { ContactsPage } from "@/components/Contacts";
import { AtendimentosPage } from "@/components/Atendimentos";

type Page = "dashboard" | "atendimentos" | "kanban" | "contacts";

const getPageTitles = (): Record<Page, string> => ({
  dashboard: i18n.t("v2.pages.dashboard"),
  atendimentos: i18n.t("v2.pages.tickets"),
  kanban: i18n.t("v2.pages.kanban"),
  contacts: i18n.t("v2.pages.contacts"),
});

const getKpiData = () => [
  {
    icon: MessageSquare,
    iconColor: "#2563EB",
    iconBg: "#EFF6FF",
    value: "420",
    label: i18n.t("v2.kpi.tickets"),
    change: "+65%",
    changePositive: true,
  },
  {
    icon: Mail,
    iconColor: "#22C55E",
    iconBg: "#F0FDF4",
    value: "1,938",
    label: i18n.t("v2.kpi.messages"),
    change: "+126",
    changePositive: true,
  },
  {
    icon: Users,
    iconColor: "#8B5CF6",
    iconBg: "#F5F3FF",
    value: "35",
    label: i18n.t("v2.kpi.activeAgents"),
    change: "+3",
    changePositive: true,
  },
  {
    icon: Clock,
    iconColor: "#F59E0B",
    iconBg: "#FFFBEB",
    value: "98.7%",
    label: i18n.t("v2.kpi.slaAchieved"),
    change: "+2.1%",
    changePositive: true,
  },
];

function DashboardContent() {
  return (
    <main className="flex flex-1 flex-col overflow-y-auto p-3 sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mx-auto max-w-[1400px] space-y-4 sm:space-y-6"
      >
        {/* Welcome Card */}
        <WelcomeCard />

        {/* KPI Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {getKpiData().map((kpi, index) => (
            <KPICard
              key={kpi.label}
              {...kpi}
              delay={0.1 + index * 0.05}
            />
          ))}
        </div>

        {/* Analytics Section */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ConversationsChart />
          </div>
          <div>
            <ChannelDonutChart />
          </div>
        </div>

        {/* Middle Section */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <RecentAttendance />
          </div>
          <div>
            <ChannelPerformance />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <AgentsOnline />
          <SLAChart />
        </div>
      </motion.div>
    </main>
  );
}

function KanbanContent() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-1 overflow-hidden"
    >
      <KanbanBoard />
    </motion.div>
  );
}

function ContactsContent() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-1 overflow-hidden"
    >
      <ContactsPage />
    </motion.div>
  );
}

function AtendimentosContent() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-1 overflow-hidden"
    >
      <AtendimentosPage />
    </motion.div>
  );
}

function AppContent() {
  const [currentPage, setCurrentPage] = useState<Page>("atendimentos");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  // Layout especial para Atendimentos - Header em cima de tudo (Sidebar + Content)
  if (currentPage === "atendimentos") {
    return (
      <div className="flex h-screen w-full flex-col bg-[var(--bg-primary)] transition-colors duration-300">
        {/* Header - Full width on top */}
        <Header pageTitle={getPageTitles()[currentPage]} onMenuClick={toggleSidebar} />

        {/* Content Area - Sidebar + Main */}
        <div className="flex flex-1 overflow-hidden gap-[3px]">
          <Sidebar
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            isOpen={sidebarOpen}
            onClose={closeSidebar}
          />
          <AtendimentosContent />
        </div>
      </div>
    );
  }

  // Layout padrão - Sidebar ocupa altura total, Header ao lado
  return (
    <div className="flex h-screen w-full bg-[var(--bg-primary)] transition-colors duration-300">
      {/* Sidebar - Full height */}
      <Sidebar
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />

      {/* Main Area - Header + Content */}
      <div className="lg:ml-[3px] flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <Header pageTitle={getPageTitles()[currentPage]} onMenuClick={toggleSidebar} />

        {/* Main Content */}
        {currentPage === "dashboard" && <DashboardContent />}
        {currentPage === "kanban" && <KanbanContent />}
        {currentPage === "contacts" && <ContactsContent />}
      </div>
    </div>
  );
}

export function App() {
  const { isAuth, loading } = useContext(AuthContext) as any;

  // Redirect to login if not authenticated
  if (!isAuth && !loading) {
    return <Redirect to="/login" />;
  }

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#F8FAFC]">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#2563EB] border-t-transparent" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}